<!-- 走势图 -->

<template>
	<div id="chart" ref="refChat"></div>
</template>

<script>
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	TitleComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { miniAwaitTo } from '@/utils/api';
import { irstore } from '@/fetch/interface';
import { mapGetters } from 'vuex';
import moment from 'moment';

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition,
	TitleComponent
]);

function splitData(rawData) {
	// 填补空值 营造前后间距假象
	const additionSide = new Array(30).fill(null);
	rawData = [...additionSide, ...rawData, ...additionSide];

	let categoryData = []; // [时间]
	let values = [];
	for (let i = 0; i < rawData.length; i++) {
		if (rawData[i]) {
			const { tradingDay, avgPrice = 0 } = rawData[i];
			categoryData.push(moment(tradingDay).format('YYYY/MM/DD'));
			values.push(avgPrice);
		} else {
			categoryData.push(null);
			values.push(null);
		}
	}
	return {
		categoryData: categoryData,
		values: values
	};
}
// 划等分
function getEqualPartsValues(array, parts) {
	const n = array.length;
	const step = Math.floor(n / parts);
	return Array.from({ length: parts }, (_, i) =>
		i === parts - 1 ? array[Math.min(step * i, n - 1)] : array[step * i]
	);
}

export default {
	data() {
		return {
			chatInstance: null
		};
	},
	computed: {
		...mapGetters(['getIrFullCode', 'getIrThemeColor'])
	},
	mounted() {},
	methods: {
		async queryQuote() {
			const defaultTime = 1;
			const daytime = new Date();
			let endTradingDay = daytime.getTime();
			let startTradingDay = daytime.setFullYear(daytime.getFullYear() - 1);
			const [err, res] = await miniAwaitTo(
				this.$axios.get(irstore.getKLineData(this.getIrFullCode, defaultTime), {
					startTradingDay,
					endTradingDay
				})
			);
			if (err) {
				console.log('😪 # queryQuote # err:', err);
				return [];
			}
			const arr = res?.data?.reverse() || [];
			return arr;
		},
		async refresh() {
			if (this.chatInstance) {
				this.chatInstance.dispose();
				this.chatInstance.clear();
				this.chatInstance = null;
			}

			const data = await this.queryQuote();
			const { categoryData, values } = splitData(data);

			this.chatInstance = echarts.init(this.$refs.refChat);

			const splitNumber = 5;
			const filterValues = values.filter(Boolean);
			// 获取坐标轴刻度最大值
			const maxVal = Number(Math.max(...filterValues)) * 1.01;
			// 获取坐标轴刻度最小值
			const minVal = Number(Math.min(...filterValues)) * 0.78;
			// 计算坐标轴分割间隔
			let interval = (maxVal - minVal) / splitNumber;

			const parts = 3;
			const filterCategory = categoryData.filter(Boolean);
			let displayPartsValues = getEqualPartsValues(filterCategory, parts);
			// 确保起点+终点
			displayPartsValues.push(filterCategory[filterCategory.length - 1]);

			// 指定图表的配置项和数据
			const option = {
				grid: {
					top: '8%',
					left: '16%',
					right: '6%',
					bottom: '10%'
				},
				xAxis: {
					type: 'category',
					data: categoryData, // 使用完整数据用于折线图
					axisLabel: {
						color: '#999',
						fontSize: 12,
						interval: 0,
						margin: 12,
						formatter: function (value, index) {
							if (displayPartsValues.includes(value)) {
								return `${value.slice(2)}`;
							}
							return '';
						}
					},
					axisTick: {
						show: false
					},
					axisLine: {
						lineStyle: {
							color: '#86909C'
						}
					}
				},
				yAxis: {
					type: 'value',
					axisLine: {
						show: false
					},
					axisLabel: {
						color: '#999',
						fontSize: 12,
						formatter: function (value) {
							return `${value.toFixed(2)}`;
						}
					},
					splitNumber: splitNumber,
					interval: interval,
					splitLine: {
						lineStyle: { color: 'rgba(0, 0, 0, 0.09)', type: 'dashed' }
					},
					min: minVal,
					max: maxVal
				},
				series: [
					{
						type: 'line',
						symbol: 'none',
						sampling: 'lttb',
						itemStyle: {
							color: this.getIrThemeColor
						},
						data: values
					}
				]
			};

			// 使用刚指定的配置项和数据显示图表。
			this.chatInstance.setOption(option);
		}
	}
};
</script>

<style lang="less" scoped>
#chart {
	width: 100%;
	height: 2.4rem;
}
</style>
